import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  catalogPlugin,
  CatalogIndexPage,
} from '@backstage/plugin-catalog';
import CatalogPage from './components/catalog/CatalogPage';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  UserSettingsPage,
  ProviderSettingsItem,
} from '@backstage/plugin-user-settings';
// Tech Radar routes are provided via EntityRadarPage and the RadarsPage list
import {
  apis,
  auth0AuthApiRef,
  acmeAuthApiRef,
  oidcAuthApiRef,
  samlAuthApiRef,
} from './apis';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { geniusTheme, geniusDarkTheme } from './theme';
import { EntityRadarPage } from './components/tech-radar/EntityRadarPage';
import { RadarsPage } from './components/tech-radar/RadarsPage';
import { OrgTeamsPage } from './components/org-teams/OrgTeamsPage';
import LogoutPage from './components/auth/LogoutPage';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes, FeatureFlagged } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { UnifiedThemeProvider } from '@backstage/theme';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogUnprocessedEntitiesPlugin } from '@backstage/plugin-catalog-unprocessed-entities';

// MUI v4 icons (aligns with @material-ui/core import)
import Star from '@material-ui/icons/Star';
import LockOpen from '@material-ui/icons/LockOpen';
import AllInclusive from '@material-ui/icons/AllInclusive';
import Security from '@material-ui/icons/Security';

// TechDocs - Mermaid plugin
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

// Devtools Frontend
import { DevToolsPage } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';

// Account Access pages
import { AccountAccessListPage } from './components/account-access/AccountAccessListPage';
import { TeamDetailsPage } from './components/account-access/TeamDetailsPage';

// GDP Services pages
import { ServicesListPage } from './components/gdp-services/ServicesListPage';
import { ServiceDetailsPage } from './components/gdp-services/ServiceDetailsPage';

// AWS Resource Tags audit
import { UntaggedAwsResourcesPage } from './components/catalog/UntaggedAwsResourcesPage';

// Certificate Management
import { CertificateManagementPage } from './components/certificate-management/CertificateManagementPage';

// StreamNative Pulsar
import { StreamNativePage } from './components/streamnative/StreamNativePage';
import { EksClustersPage } from './components/eks-clusters/EksClustersPage';

// ASPM Plugin
import { AspmFrontendPage,AspmDashboard } from '@appsecgenius/plugin-aspm-frontend';

// Budgeting
// import { BudgetingPage } from './components/budgeting/BudgetingPage';

const app = createApp({
  apis,
  plugins: [catalogUnprocessedEntitiesPlugin],
  themes: [
    {
      id: 'genius',
      title: 'Genius Sports',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={geniusTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'genius-dark',
      title: 'Genius Sports Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={geniusDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  featureFlags: [
    {
      name: 'devtools-enabled',
      pluginId: 'devtools',
      description: 'Enable DevTools plugin and sidebar item',
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const isLogoutPath = globalThis.window.location?.pathname === '/logout';
      // Allow LogoutPage to disable auto sign-in even if the path changes
      // after reload by using a one-time sessionStorage flag.
      const disableOnce =
        globalThis.window.sessionStorage?.getItem('disableAutoSignInOnce') ===
        'true';
      if (disableOnce) {
        try {
          globalThis.window.sessionStorage.removeItem('disableAutoSignInOnce');
        } catch {
          // ignore storage errors
        }
      }
      const disableAuto = isLogoutPath || disableOnce;
      const env =
        configApi.getOptionalString('auth.environment') ?? 'development';
      if (env === 'development') {
        return (
          <SignInPage {...props} providers={['guest']} auto={!disableAuto} />
        );
      }
      return (
        <SignInPage
          {...props}
          provider={{
            id: 'auth0',
            title: 'Auth0',
            message: disableAuto
              ? 'You have been signed out. Click to sign in again.'
              : 'Sign In using Auth0',
            apiRef: auth0AuthApiRef,
          }}
          auto={!disableAuto}
        />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    {/* Hidden binding to satisfy catalog root routeRef for components that link back to the catalog */}
    <Route path="/catalog-index" element={<CatalogIndexPage />} />
    <Route path="/catalog" element={<CatalogPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid />
      </TechDocsAddons>
    </Route>
    <Route path="/aspm-dashboard" element={<AspmDashboard />} />
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    {/* Logout route to clear session and show a non-auto sign-in screen on reload */}
    <Route path="/logout" element={<LogoutPage />} />

    {/* User Settings with provider settings merged in */}
    <Route
      path="/settings"
      element={
        <UserSettingsPage
          providerSettings={
            <>
              <ProviderSettingsItem
                title="ACME"
                description="Provides sign-in via ACME"
                apiRef={acmeAuthApiRef}
                icon={Star}
              />
              <ProviderSettingsItem
                title="OIDC"
                description="OpenID Connect provider"
                apiRef={oidcAuthApiRef}
                icon={LockOpen}
              />
              <ProviderSettingsItem
                title="Auth0"
                description="Sign in with Auth0"
                apiRef={auth0AuthApiRef}
                icon={AllInclusive}
              />
              <ProviderSettingsItem
                title="SAML"
                description="SAML-based sign-in"
                apiRef={samlAuthApiRef}
                icon={Security}
              />
            </>
          }
        />
      }
    />

    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/tech-radar/:namespace/:kind/:name"
      element={<EntityRadarPage />}
    />
    <Route path="/tech-radar" element={<RadarsPage />} />
    <Route path="/org-teams" element={<OrgTeamsPage />} />
    <Route path="/account-access" element={<AccountAccessListPage />} />
    <Route path="/account-access/:teamId" element={<TeamDetailsPage />} />
    <Route path="/gdp-services" element={<ServicesListPage />} />
    <Route path="/gdp-services/:serviceName" element={<ServiceDetailsPage />} />
    <Route path="/aws-resource-tags" element={<UntaggedAwsResourcesPage />} />
    <Route path="/aspm-frontend" element={<AspmFrontendPage />} />
    <Route
      path="/certificate-management"
      element={<CertificateManagementPage />}
    />
    <Route path="/streamnative" element={<StreamNativePage />} />
    <Route path="/eks-clusters" element={<EksClustersPage />} />
    {/* <Route path="/budgeting" element={<BudgetingPage />} /> */}
    <Route
      path="/certificate-management"
      element={<CertificateManagementPage />}
    />
    <Route path="/rbac" element={<RbacPage />} />
    <Route
      path="/devtools"
      element={
        <FeatureFlagged with="devtools-enabled">
          <DevToolsPage />
        </FeatureFlagged>
      }
    >
      {customDevToolsPage}
    </Route>
    <Route path="/aspm-dashboard" element={<AspmDashboard />} />
    <Route path="/aspm-frontend" element={<AspmFrontendPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
